/**
 * Family defaults — the template every device inherits.
 *
 * Same editor this page has always been, reframed once per-device overrides
 * landed. The one structural addition is the inheritance count on each
 * section: a parent editing the taxonomy needs to know who they are about to
 * affect and, more importantly, who they are not. Without it, editing a
 * section that a child's device has overridden looks like the save silently
 * failed.
 *
 * Saving writes the family row and then fans out a fresh resolved document
 * to every enrolled device.
 */

import { useEffect, useState } from 'preact/hooks';
import {
  DEFAULT_CONFIG,
  clearCachedConfig,
  currentConfig,
  fetchLatest,
  save,
  type DeviceOverride,
  type PlaintextConfig,
  type BlocklistEntry,
  type WeeklySchedule,
} from '../data/config';
import { endpoints, refresh } from '../data/endpoints';
import { syncDeviceConfigs } from '../data/deviceConfig';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ScheduleEditor } from '../components/ScheduleEditor';
import { TaxonomyEditor } from '../components/TaxonomyEditor';

/**
 * Extract the current blocklist textarea value from a config. We only edit
 * the first blocklist for v1; URL-pattern entries pass through unchanged in
 * the raw data but are not rendered here.
 */
function blocklistToText(cfg: PlaintextConfig): string {
  const first = cfg.blocklists[0];
  if (!first) return '';
  return first.entries
    .map((e) => ('domain' in e ? e.domain : ''))
    .filter((s) => s.length > 0)
    .join('\n');
}

/** Parse a textarea value into `{domain}` blocklist entries, trimming and de-duping empties. */
function textToBlocklist(text: string): BlocklistEntry[] {
  const seen = new Set<string>();
  const out: BlocklistEntry[] = [];
  for (const line of text.split('\n')) {
    const host = line.trim();
    if (host.length === 0) continue;
    if (seen.has(host)) continue;
    seen.add(host);
    out.push({ domain: host });
  }
  return out;
}

/**
 * "3 of 4 devices inherit this" for one section.
 *
 * The count is doing real work, not decoration: it is the difference between
 * a parent trusting this screen and a parent editing the taxonomy, seeing
 * nothing change on the child they cared about, and concluding the product
 * is broken.
 */
function InheritCount({
  section,
  overrides,
  deviceCount,
}: {
  section: keyof DeviceOverride;
  overrides: Record<string, DeviceOverride>;
  deviceCount: number;
}) {
  if (deviceCount === 0) return null;
  const overriding = Object.values(overrides).filter(
    (ov) => ov[section] !== undefined,
  ).length;
  const inheriting = deviceCount - overriding;
  return (
    <Badge tone={overriding > 0 ? 'warn' : 'neutral'}>
      {inheriting} of {deviceCount} device{deviceCount === 1 ? '' : 's'} inherit
    </Badge>
  );
}

export function ConfigRoute() {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'ok' | 'partial' | 'error'
  >('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await fetchLatest();
        // The device list only feeds the inheritance counts. If it fails,
        // the editor must still work — losing "3 of 4 devices inherit" is a
        // worse outcome than a blank page only if you can't tell them apart.
        await refresh().catch((e: unknown) => {
          console.warn('[config] device list unavailable', e);
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cfg: PlaintextConfig = currentConfig.value ?? DEFAULT_CONFIG;
  const devices = endpoints.value.filter((e) => e.status !== 'revoked');
  const overrides = cfg.device_overrides;

  function update(next: PlaintextConfig): void {
    currentConfig.value = next;
    setSaveStatus('idle');
  }

  function updateBlocklistText(text: string): void {
    const entries = textToBlocklist(text);
    const blocklists =
      entries.length > 0 ? [{ id: 'main', label: 'Main', entries }] : [];
    update({ ...cfg, blocklists });
  }

  function updateActiveCadence(active_seconds: number): void {
    update({ ...cfg, cadence: { ...cfg.cadence, active_seconds } });
  }

  function updateSchedule(schedule: WeeklySchedule): void {
    update({ ...cfg, schedule });
  }

  async function onSave(): Promise<void> {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      await save(cfg);
      // Every device's resolved document changes when the defaults do —
      // except for the sections it overrides, which `resolve()` preserves.
      const results = await syncDeviceConfigs(devices.map((e) => e.id));
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setSaveStatus('partial');
        setSaveError(
          `Saved, but ${String(failed.length)} of ${String(results.length)} devices did not update. They will retry on the next dashboard refresh.`,
        );
        return;
      }
      setSaveStatus('ok');
    } catch (e) {
      setSaveStatus('error');
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onReloadAndRetry(): Promise<void> {
    setSaveStatus('idle');
    setSaveError(null);
    await clearCachedConfig();
    await fetchLatest();
  }

  return (
    <main class="mx-auto max-w-4xl space-y-6 p-4">
      <div>
        <h1 class="text-3xl font-semibold">Family defaults</h1>
        <p class="text-surface-muted mt-1 max-w-[70ch] text-sm">
          The starting point every device inherits. A device that has customised
          a section keeps its own value and ignores changes here — open it from
          the dashboard to see or change that.
        </p>
      </div>

      {loading ? <p class="text-sm text-surface-muted">Loading…</p> : null}

      <section class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-medium">Harms taxonomy</h2>
          <InheritCount
            section="taxonomy"
            overrides={overrides}
            deviceCount={devices.length}
          />
        </div>
        <p class="text-surface-muted max-w-[70ch] text-sm">
          Each category is a prose block the AI reads and applies literally.
          Include what to flag AND what NOT to flag — the "enough is enough"
          line lives in the words, not in a numeric threshold.
        </p>
        <TaxonomyEditor
          value={cfg.taxonomy}
          onChange={(taxonomy) => {
            update({ ...cfg, taxonomy });
          }}
        />
      </section>

      <section class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-medium">Blocklist</h2>
          <InheritCount
            section="blocklists"
            overrides={overrides}
            deviceCount={devices.length}
          />
        </div>
        <p class="text-surface-muted text-sm">
          One host per line (e.g. <code>minecraft.net</code>). URL-pattern
          entries land in v1.1.
        </p>
        <label class="block text-sm">
          <span class="sr-only">Blocklist domains</span>
          <textarea
            aria-label="Blocklist domains"
            class="border-surface-muted/40 bg-surface h-32 w-full rounded border p-2 font-mono text-sm"
            value={blocklistToText(cfg)}
            onInput={(e) => {
              updateBlocklistText((e.target as HTMLTextAreaElement).value);
            }}
          />
        </label>
      </section>

      <section class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-medium">Schedule</h2>
          <InheritCount
            section="schedule"
            overrides={overrides}
            deviceCount={devices.length}
          />
        </div>
        <p class="text-surface-muted max-w-[70ch] text-sm">
          Active capture windows in the endpoint's local time. Overrides the
          legacy "active hours" window when any day has a configured window;
          clear every day to fall back. Pausing a device is the master override
          — schedule only applies when collection is on.
        </p>
        <ScheduleEditor value={cfg.schedule} onChange={updateSchedule} />
      </section>

      <section class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-medium">Cadence</h2>
          <InheritCount
            section="cadence"
            overrides={overrides}
            deviceCount={devices.length}
          />
        </div>
        <label class="flex items-center gap-2 text-sm">
          Screenshot interval (seconds, active hours)
          <input
            type="number"
            min={30}
            max={3600}
            value={cfg.cadence.active_seconds}
            class="border-surface-muted/40 bg-surface h-9 w-24 rounded border px-2 text-sm"
            onInput={(e) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n) && n > 0) updateActiveCadence(n);
            }}
          />
        </label>
      </section>

      <Card class="bg-surface-2 text-surface-muted text-sm">
        {devices.length === 0
          ? 'No devices enrolled yet — these defaults apply to the first one you add.'
          : `Saving pushes updated settings to ${String(devices.length)} device${devices.length === 1 ? '' : 's'}, skipping any section a device has customised.`}
      </Card>

      <div class="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => void onSave()}
          disabled={saveStatus === 'saving'}
        >
          {saveStatus === 'saving' ? 'Saving…' : 'Save defaults'}
        </Button>
        {saveStatus === 'ok' ? (
          <span class="text-sm text-emerald-700">Saved.</span>
        ) : null}
        {saveStatus === 'partial' && saveError !== null ? (
          <span class="text-sm text-amber-700">{saveError}</span>
        ) : null}
        {saveStatus === 'error' && saveError !== null ? (
          <div class="flex items-center gap-2 text-sm">
            <span class="text-signal">Save failed: {saveError}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void onReloadAndRetry()}
            >
              Reload latest
            </Button>
          </div>
        ) : null}
      </div>
    </main>
  );
}
